import { lazy, Suspense, useMemo } from 'react';
import { QueryClientProvider } from '@tanstack/react-query';
import { BrowserRouter, Navigate, Route, Routes } from 'react-router-dom';
import { Spinner, Toaster } from '@moveek/ui';

import type { BookingInitialState, MountProps, VoucherInitialState } from './types';
import { createQueryClient } from './shared/queryClient';
import { FullPageError } from './shared/FullPageError';
import { ErrorBoundary } from './shared/ErrorBoundary';

import { BookingFlowHeaderSlot } from './booking/shared/BookingFlowHeader';
import { CheckoutSkeleton } from './booking/checkout/CheckoutSkeleton';

const SeatsPage = lazy(() => import('./booking/seats/SeatsPage'));
const ConcessionsPage = lazy(() => import('./booking/concessions/ConcessionsPage'));
const CheckoutPage = lazy(() => import('./booking/checkout/CheckoutPage'));
const BookingViewPage = lazy(() => import('./booking/view/BookingViewPage'));
const VoucherSuccessPage = lazy(() =>
  import('./booking/voucher/VoucherSuccessPage').then((m) => ({ default: m.VoucherSuccessPage })),
);
const VoucherFlow = lazy(() => import('./booking/voucher/VoucherFlow'));

function RouteFallback() {
  return (
    <div className="mvk-root flex min-h-screen items-center justify-center" aria-busy>
      <Spinner />
    </div>
  );
}

function VoucherShell({ state }: { state: VoucherInitialState }) {
  return (
    <div className="mvk-root min-h-screen p-6">
      <h1 className="mvk-display text-2xl">Voucher — {state.voucherSlug}</h1>
      <p className="mt-2 text-sm text-fg-subtle">
        Voucher flow chưa được cài đặt (Phase 4).
      </p>
    </div>
  );
}

export function App(props: MountProps) {
  const queryClient = useMemo(() => createQueryClient(), []);

  let content;
  if (props.mode === 'booking') {
    const state = props.initialState as BookingInitialState;
    content = (
      <>
        <BookingFlowHeaderSlot backHref={props.routes?.movie} />
        <Suspense fallback={<RouteFallback />}>
          <Routes>
            <Route index element={<Navigate to="seats" replace />} />
            <Route
              path="seats"
              element={
                <SeatsPage
                  showtime={state.showtime}
                  apiBase={props.apiBase}
                  backHref={props.routes?.movie}
                  orderId={state.orderId}
                  showOptinToggle={props.flags.showBookingAppV2OptinToggle}
                />
              }
            />
            <Route
              path="concessions"
              element={
                <ConcessionsPage
                  showtime={state.showtime}
                  apiBase={props.apiBase}
                  orderId={state.orderId}
                />
              }
            />
            <Route
              path="checkout"
              element={
                // Own Suspense boundary so the checkout chunk loads behind a
                // layout-shaped skeleton instead of the generic RouteFallback spinner.
                <Suspense fallback={<CheckoutSkeleton />}>
                  <CheckoutPage
                    showtime={state.showtime}
                    apiBase={props.apiBase}
                    user={props.user}
                    orderId={state.orderId}
                  />
                </Suspense>
              }
            />
          </Routes>
        </Suspense>
      </>
    );
  } else if (props.mode === 'view') {
    // Both post-gateway view pages share mode='view' with a standalone mount
    // (basename = the view path, order id read from `:orderId`). The voucherV2
    // flag — set true only by VoucherV2Controller's view mount, false by the
    // booking one — selects the voucher success page over the booking ticket view.
    const ViewPage = props.flags.voucherV2 ? VoucherSuccessPage : BookingViewPage;
    content = (
      <Suspense fallback={<RouteFallback />}>
        <Routes>
          <Route path=":orderId" element={<ViewPage apiBase={props.apiBase} />} />
        </Routes>
      </Suspense>
    );
  } else {
    const state = props.initialState as VoucherInitialState;
    content = props.flags.voucherV2 ? (
      <Suspense fallback={<RouteFallback />}>
        <VoucherFlow state={state} apiBase={props.apiBase} user={props.user} />
      </Suspense>
    ) : (
      <VoucherShell state={state} />
    );
  }

  return (
    <ErrorBoundary fallback={<FullPageError />}>
      <QueryClientProvider client={queryClient}>
        <BrowserRouter basename={props.basePath}>{content}</BrowserRouter>
        <Toaster />
      </QueryClientProvider>
    </ErrorBoundary>
  );
}
